import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Pagination } from "@hiya/ui";
import {
  listNotifications,
  markAsRead,
} from "../../../api/notifications";
import type { NotificationItem } from "../../../types/notification";
import { formatDateTime } from "../../../utils/formatDate";
import { CenteredMessage } from "../../../components/CenteredMessage";

const PAGE_SIZE = 20;

function NotificationRow({
  item,
  onRead,
}: Readonly<{
  item: NotificationItem;
  onRead: (id: string) => void;
}>) {
  const navigate = useNavigate();

  function handleClick() {
    if (!item.is_read) {
      onRead(item.id);
    }
    // Navigate based on reference_type
    if (item.reference_type === "consultation" && item.reference_id) {
      navigate(`/dashboard/consultations/${item.reference_id}`);
    } else if (
      (item.reference_type === "report" || item.reference_type === "kit") &&
      item.reference_id
    ) {
      navigate(`/dashboard/results/${item.reference_id}`);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex w-full text-left p-4 border-b border-stroke-primary hover:bg-bg-secondary transition-colors"
    >
      <div className="flex-1 min-w-0">
        <p
          className={`text-base font-medium leading-[1.3] ${
            item.is_read ? "text-text-primary" : "text-brand-brown"
          }`}
        >
          {item.title}
        </p>
        <p className="text-sm text-text-secondary mt-1 leading-[1.4]">
          {item.body}
        </p>
      </div>
      <div className="flex items-center gap-3 shrink-0 ml-4">
        <span className="text-sm text-text-tertiary whitespace-nowrap">
          {formatDateTime(item.created_at)}
        </span>
        {!item.is_read && (
          <span className="size-2 rounded-full bg-brand-brown shrink-0" />
        )}
      </div>
    </button>
  );
}

interface NotificationListProps {
  items: NotificationItem[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRead: (id: string) => void;
}

function NotificationList({ items, page, totalPages, onPageChange, onRead }: Readonly<NotificationListProps>) {
  return (
    <>
      <div className="border border-stroke-primary rounded-xl overflow-hidden">
        {items.map((item) => (
          <NotificationRow key={item.id} item={item} onRead={onRead} />
        ))}
      </div>
      <div className="mt-5">
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
      </div>
    </>
  );
}

export function NotificationsPage() {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (p: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await listNotifications(p, PAGE_SIZE);
      setItems(data.items);
      setTotal(data.total);
    } catch {
      setError("Something went wrong. Please refresh the page.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [load, page]);

  function handleRead(id: string) {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, is_read: true } : it)),
    );
    markAsRead(id).catch(() => {
      // Revert on failure
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, is_read: false } : it)),
      );
    });
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      {loading && (
        <div className="flex flex-col">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[72px] bg-bg-secondary animate-pulse mb-2 rounded-xl"
            />
          ))}
        </div>
      )}

      {!loading && error && <CenteredMessage message={error} />}

      {!loading && !error && items.length === 0 && (
        <CenteredMessage message="No notifications yet" />
      )}

      {!loading && !error && items.length > 0 && (
        <NotificationList
          items={items}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onRead={handleRead}
        />
      )}
    </div>
  );
}
